import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './WYSIWYGEditor.css';
import { uploadImageToStorage } from '../services/firebaseBlogService';

const WYSIWYGEditor = ({ value, onChange, theme, placeholder }) => {
  const quillRef = useRef(null);
  
  // Custom toolbar configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        // Text formatting
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        
        // Text style
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        
        // Alignment and indentation
        [{ 'align': [] }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        
        // Lists
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        
        // Insert elements
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        
        // Clear formatting
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  // Custom image handler - uploads to Firebase Storage
  function imageHandler() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          alert('File size exceeds 10MB limit');
          return;
        }

        try {
          // Show uploading indicator
          const quill = this.quill || quillRef.current?.getEditor();
          if (!quill) return;
          
          const range = quill.getSelection(true);
          const loadingId = `loading-${Date.now()}`;
          
          // Insert loading placeholder
          quill.insertEmbed(range.index, 'image', 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7');
          quill.setSelection(range.index + 1);
          
          // Upload to Firebase Storage
          const result = await uploadImageToStorage(file, 'blog-content-images');
          
          // Replace loading placeholder with actual image
          const editor = quillRef.current?.getEditor();
          if (editor) {
            const delta = editor.getContents();
            const ops = delta.ops;
            for (let i = 0; i < ops.length; i++) {
              if (ops[i].insert?.image?.includes('data:image/gif')) {
                editor.deleteText(i, 1);
                editor.insertText(i, '');
                editor.format('image', result.url);
                break;
              }
            }
            // More reliable replacement
            editor.root.innerHTML = editor.root.innerHTML.replace(
              'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
              result.url
            );
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          alert('Failed to upload image. Please try again.');
        }
      }
    };
  }

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align', 'indent',
    'list', 'bullet',
    'script',
    'blockquote', 'code-block',
    'link', 'image', 'video',
    'clean'
  ];

  return (
    <div className={`wysiwyg-editor-wrapper ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder || 'Start writing your blog post here...'}
        className="wysiwyg-editor"
      />
      <div className={`editor-tips ${theme === 'dark' ? 'dark' : 'light'}`}>
        <strong>💡 Tips:</strong> Use the toolbar above to format your content. 
        Click the image icon (🖼️) to upload images directly to Firebase Storage. 
        Your content is automatically saved as you type!
      </div>
    </div>
  );
};

export default WYSIWYGEditor;